/** Agent-wide client state shared by the shell and the pages. */
import { useSyncExternalStore } from "react";
import type { AgentSettings, ApprovalView, MachineView, MissionRunView } from "../../shared/contracts";
import { api } from "./api";
import { isRunActive } from "./format";
import { agentSocket } from "./socket";
import { invalidate, mutateResource, useResource } from "./store";

export function useSettings() {
  return useResource<AgentSettings>("settings", (signal) => api("GET /api/settings", { signal }), {
    refreshOn: ["settings.updated"],
    staleMs: 15_000,
  });
}

export function usePendingApprovals() {
  return useResource<ApprovalView[]>(
    "approvals:pending",
    async (signal) => (await api("GET /api/approvals", { query: { status: "pending" }, signal })).approvals,
    { refreshOn: ["approval.upsert"] },
  );
}

export function useMachines() {
  return useResource<MachineView[]>("machines", async (signal) => (await api("GET /api/machines", { signal })).machines, {
    refreshOn: ["machine.upsert"],
  });
}

const fetchRuns = async (signal: AbortSignal) => (await api("GET /api/runs", { query: { limit: "50" }, signal })).runs;

/**
 * Recent mission runs (newest first), shared by the missions pages and the agent's presence. `poll` (the missions
 * pages): revalidate on every mount, and every 10 s while a run is active even if an event is missed. Without it
 * (the presence, mounted in many places): run.upsert events keep it fresh, and a mount refetches only stale data.
 */
export function useRuns({ poll = true }: { poll?: boolean } = {}) {
  return useResource<MissionRunView[]>("runs", fetchRuns, {
    refreshOn: ["run.upsert"],
    pollMs: poll ? (runs) => (runs?.some((r) => isRunActive(r.status)) ? 10_000 : false) : false,
    staleMs: poll ? 0 : 60_000,
  });
}

/** A machine as the server just returned it: replaced in (or added to) the cached list; the dashboard refetches. */
export function upsertMachine(machine: MachineView): void {
  mutateResource<MachineView[]>("machines", (list) => {
    if (!list) return [machine];
    return list.some((m) => m.id === machine.id) ? list.map((m) => (m.id === machine.id ? machine : m)) : [...list, machine];
  });
  invalidate("dashboard");
}

// ── Live "the agent is working" indicator, fed by turn.started / turn.done ──

const TURN_TTL_MS = 10 * 60_000;
const running = new Map<string, { trigger: string; since: number }>();
const listeners = new Set<() => void>();
let snapshot = false;
/** Trigger of the newest running turn ("user_message", "morning_brief"…), null when idle. */
let triggerSnapshot: string | null = null;

function recompute(): void {
  const now = Date.now();
  for (const [id, t] of running) if (now - t.since > TURN_TTL_MS) running.delete(id);
  const next = running.size > 0;
  const newest = [...running.values()].sort((a, b) => b.since - a.since)[0];
  const trigger = newest ? newest.trigger : null;
  if (next !== snapshot || trigger !== triggerSnapshot) {
    snapshot = next;
    triggerSnapshot = trigger;
    for (const fn of [...listeners]) fn();
  }
}

function subscribeActivity(fn: () => void): () => void {
  listeners.add(fn);
  const t = setInterval(recompute, 60_000);
  return () => {
    listeners.delete(fn);
    clearInterval(t);
  };
}

agentSocket.subscribe((event) => {
  if (event.type === "turn.started") running.set(event.turnId, { trigger: event.trigger, since: Date.now() });
  else if (event.type === "turn.done") running.delete(event.turnId);
  else if (event.type === "settings.updated") mutateResource<AgentSettings>("settings", () => event.settings);
  else return;
  recompute();
});

// After a reconnection we may have missed turn.done events: start from a clean slate.
agentSocket.onOpen((reconnect) => {
  if (!reconnect) return;
  running.clear();
  recompute();
});

export function resetAgentActivity(): void {
  running.clear();
  recompute();
}

export function useAgentWorking(): boolean {
  return useSyncExternalStore(
    subscribeActivity,
    () => snapshot,
    () => snapshot,
  );
}

/** Why the agent is working right now (the trigger of its newest running turn), null when idle. */
export function useAgentTrigger(): string | null {
  return useSyncExternalStore(
    subscribeActivity,
    () => triggerSnapshot,
    () => triggerSnapshot,
  );
}
